import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimerControlService } from '../../../service/timer-control.service';
import { MatDialog } from '@angular/material/dialog';
import { GameOverDialogComponent } from '../../dialogs/gameover-dialog/game-over-dialog.component';
import {
  COUNTDOWN_STORAGE_KEY,
  COUNTDOWN_STORAGE_PAUSED_KEY,
  COUNTDOWN_STORAGE_TIMESTAMP_KEY,
  COUNTDOWN_TIMEOUT,
} from '../../../config/constants';
import { GameService } from '../../../service/game.service';
import { Route } from '../../../enum/route.enum';
import { Router } from '@angular/router';

@Component({
  selector: 'gandalf-countdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './countdown.component.html',
  styleUrl: './countdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownComponent implements OnDestroy {
  public readonly isRunning = signal(false);
  private readonly timerControl = inject(TimerControlService);
  private gameControlService = inject(GameService);
  private router = inject(Router);
  private countdownInterval: number | null = null;
  private readonly STORAGE_KEY = COUNTDOWN_STORAGE_KEY;
  private readonly STORAGE_TIMESTAMP_KEY = COUNTDOWN_STORAGE_TIMESTAMP_KEY;
  private readonly STORAGE_PAUSED_KEY = COUNTDOWN_STORAGE_PAUSED_KEY;
  private remainingSeconds = signal(COUNTDOWN_TIMEOUT);
  public readonly formattedTime = computed(() => {
    const total = this.remainingSeconds();
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${this.formatTime(minutes)}:${this.formatTime(seconds)}`;
  });
  private dialog = inject(MatDialog);

  constructor() {
    // Restore countdown from localStorage
    const stored = localStorage.getItem(this.STORAGE_KEY);
    const storedTimestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);
    const wasPaused = localStorage.getItem(this.STORAGE_PAUSED_KEY) === 'true';

    if (stored && storedTimestamp) {
      let restored: number;

      if (wasPaused) {
        // If countdown was paused, don't calculate elapsed time - just use stored value
        restored = Number(stored);
      } else {
        // If countdown was running, calculate elapsed time
        const elapsed = Math.floor((Date.now() - Number(storedTimestamp)) / 1000);
        restored = Math.max(Number(stored) - elapsed, 0);
      }

      this.remainingSeconds.set(restored);

      // Only auto-start if we're on the game route AND there's time remaining AND it wasn't paused
      const isGameRoute = this.router.url.includes(Route.GAME);
      if (restored > 0 && isGameRoute && !wasPaused) {
        this.isRunning.set(true);
        this.startCountdown();
      } else if (restored === 0) {
        this.stopCountdown();
      }
    }
    effect(() => {
      if (this.timerControl.startCountdownSignal()) {
        this.startCountdown();
        this.timerControl.resetAllSignals();
      }
    });
    effect(() => {
      if (this.timerControl.stopCountdownSignal()) {
        this.stopCountdown();
        this.timerControl.resetAllSignals();
      }
    });
    effect(() => {
      if (this.timerControl.resetCountdownSignal()) {
        this.resetCountdown();
        this.timerControl.resetAllSignals();
      }
    });
  }

  public startCountdown(): void {
    // Check if interval is actually running, not just the flag
    if (this.countdownInterval === null) {
      // Clear any stale state
      this.isRunning.set(true);
      this.saveCountdownState();
      this.countdownInterval = window.setInterval(() => {
        if (this.remainingSeconds() > 0) {
          this.remainingSeconds.set(this.remainingSeconds() - 1);
          this.saveCountdownState();
        } else {
          this.stopCountdown();
          this.gameOver();
        }
      }, 1000);
    }
  }

  public stopCountdown(): void {
    this.isRunning.set(false);
    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    // Save state when stopping to persist the current time
    this.saveCountdownState();
  }

  public resetCountdown(): void {
    this.stopCountdown();
    this.remainingSeconds.set(COUNTDOWN_TIMEOUT);
    this.isRunning.set(false); // Explicitly ensure flag is false
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    localStorage.removeItem(this.STORAGE_PAUSED_KEY);
  }

  public ngOnDestroy(): void {
    this.stopCountdown();
  }

  public gameOver(): void {
    this.dialog.open(GameOverDialogComponent, {
      disableClose: true,
      width: '80%',
      panelClass: 'animateIn',
      backdropClass: 'backdropAnimateIn',
    });
    // Clear countdown state after game over
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    localStorage.removeItem(this.STORAGE_PAUSED_KEY);
    this.gameControlService.restartGame();
  }

  private saveCountdownState(): void {
    localStorage.setItem(this.STORAGE_KEY, this.remainingSeconds().toString());
    localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
    // Save paused status - if not running, it's paused
    localStorage.setItem(this.STORAGE_PAUSED_KEY, (!this.isRunning()).toString());
  }

  private formatTime(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
